import React, { useEffect,  useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useUIStore } from '../../store/uiStore'
import { useAuthStore } from '../../store/authStore'
import { useNavCategories } from '../../hooks/useNavCategories'
import { useFocusTrap } from '../../hooks/useFocusTrap'
import { SearchBar } from '../molecules/SearchBar'
import type { NavCategory } from '../../types/nav.types'

export const MobileNav: React.FC = () => {
  const { mobileNavOpen, closeMobileNav } = useUIStore()
  const { isAuthenticated, user, clearAuth } = useAuthStore()
  const { categories } = useNavCategories()
  const location = useLocation()
  const drawerRef = useFocusTrap(mobileNavOpen) as React.RefObject<HTMLDivElement>
  const [expandedCategory, setExpandedCategory] = React.useState<string | null>(null)

  // Close on route change
  useEffect(() => { closeMobileNav() }, [location.pathname, closeMobileNav])

  // Prevent body scroll when open
  useEffect(() => {
    if (mobileNavOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [mobileNavOpen])

  // Escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobileNav()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [closeMobileNav])

  const toggleCategory = useCallback((slug: string) => {
    setExpandedCategory((prev) => (prev === slug ? null : slug))
  }, [])

  const handleLogout = () => {
    clearAuth()
    closeMobileNav()
  }

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden="true"
        onClick={closeMobileNav}
        className={`
          fixed inset-0 bg-charcoal/50 z-40 lg:hidden
          transition-opacity duration-200
          ${mobileNavOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        className={`
          fixed top-0 left-0 bottom-0 w-[320px] max-w-[90vw]
          bg-white z-50 overflow-y-auto
          flex flex-col
          lg:hidden
          transform transition-transform duration-200 ease-out
          ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-mid-gray shrink-0">
          <Link to="/" onClick={closeMobileNav} className="focus-visible:outline-none focus-visible:underline">
            <span className="font-display font-bold text-heading-3 text-charcoal">
              One<span className="text-royal-orange">Tech</span>
            </span>
          </Link>
          <button
            onClick={closeMobileNav}
            aria-label="Close navigation menu"
            className="
              w-8 h-8 flex items-center justify-center rounded-sm
              text-charcoal hover:text-royal-orange
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-orange
            "
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-mid-gray shrink-0">
          <SearchBar onClose={closeMobileNav} />
        </div>

        {/* Auth section — guest */}
        {!isAuthenticated && (
          <div className="flex gap-2 px-4 py-3 border-b border-mid-gray shrink-0">
            <Link
              to="/login"
              onClick={closeMobileNav}
              className="
                flex-1 py-2.5 text-center
                border border-charcoal text-charcoal font-semibold text-body-base rounded-sm
                hover:bg-light-gray transition-colors duration-150
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal
              "
            >
              Login
            </Link>
            <Link
              to="/register"
              onClick={closeMobileNav}
              className="
                flex-1 py-2.5 text-center
                bg-royal-orange text-white font-semibold text-body-base rounded-sm
                hover:bg-royal-orange-hover transition-colors duration-150
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-orange
              "
            >
              Register
            </Link>
          </div>
        )}

        {/* Auth section — authenticated */}
        {isAuthenticated && (
          <div className="px-4 py-3 border-b border-mid-gray shrink-0">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-full bg-burgundy text-white flex items-center justify-center font-bold text-body-base shrink-0">
                {user?.full_name?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div className="overflow-hidden">
                <p className="font-semibold text-body-base text-charcoal truncate">{user?.full_name}</p>
                <p className="text-caption text-dark-gray truncate">{user?.email}</p>
              </div>
            </div>
            <nav className="flex flex-col">
              <MobileNavLink to="/account" label="My Account" onClick={closeMobileNav} />
              <MobileNavLink to="/account/orders" label="Orders" onClick={closeMobileNav} />
              <MobileNavLink to="/account/wishlist" label="Wishlist" onClick={closeMobileNav} />
              {user?.role === 'admin' && (
                <MobileNavLink to="/dashboard" label="Admin Dashboard" onClick={closeMobileNav} />
              )}
            </nav>
          </div>
        )}

        {/* Categories accordion */}
        <nav
          aria-label="Product categories"
          className="flex-1 overflow-y-auto px-0 py-2"
        >
          <p className="px-4 py-2 text-caption text-dark-gray uppercase tracking-widest font-semibold">
            Shop
          </p>

          {(categories as any).data?.map((cat: NavCategory) => (
            <CategoryAccordion
              key={cat.id}
              category={cat}
              expanded={expandedCategory === cat.slug}
              onToggle={() => toggleCategory(cat.slug)}
              onNavigate={closeMobileNav}
            />
          ))}

          <div className="px-4 py-1.5">
            <MobileNavLink to="/deals" label="Deals & Offers" onClick={closeMobileNav} />
          </div>
          <div className="px-4 py-1.5">
            <MobileNavLink to="/brands" label="All Brands" onClick={closeMobileNav} />
          </div>
        </nav>

        {/* Footer strip */}
        {isAuthenticated && (
          <div className="shrink-0 border-t border-mid-gray px-4 py-4">
            <button
              onClick={handleLogout}
              className="
                w-full py-2.5 text-body-base text-error-red font-semibold
                border border-error-red rounded-sm
                hover:bg-red-50 transition-colors duration-150
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error-red
              "
            >
              Sign Out
            </button>
          </div>
        )}
      </div>
    </>
  )
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface CategoryAccordionProps {
  category: NavCategory
  expanded: boolean
  onToggle: () => void
  onNavigate: () => void
}

const CategoryAccordion: React.FC<CategoryAccordionProps> = ({
  category,
  expanded,
  onToggle,
  onNavigate,
}) => {
  const hasChildren = (category.children?.length ?? 0) > 0
  const panelId = `mobile-cat-panel-${category.slug}`

  return (
    <div className="border-b border-mid-gray/40 last:border-0">
      {hasChildren ? (
        <>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-controls={panelId}
            className="
              w-full flex items-center justify-between
              px-4 py-3 text-body-base font-semibold text-charcoal
              hover:text-royal-orange hover:bg-light-gray
              transition-colors duration-100
              focus-visible:outline-none focus-visible:bg-light-gray
            "
          >
            {category.name}
            <svg
              className={`w-4 h-4 text-dark-gray transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {expanded && (
            <ul id={panelId} className="bg-light-gray/50 py-1 animate-fade-in" role="list">
              <li>
                <Link
                  to={`/shop/${category.slug}`}
                  onClick={onNavigate}
                  className="
                    block px-8 py-2.5 text-body-base text-royal-orange font-semibold
                    hover:bg-light-gray transition-colors duration-100
                    focus-visible:outline-none focus-visible:bg-light-gray
                  "
                >
                  All {category.name} →
                </Link>
              </li>
              {category.children?.map((sub) => (
                <li key={sub.id}>
                  <Link
                    to={`/shop/${category.slug}/${sub.slug}`}
                    onClick={onNavigate}
                    className="
                      block px-8 py-2.5 text-body-base text-charcoal
                      hover:text-royal-orange hover:bg-light-gray
                      transition-colors duration-100
                      focus-visible:outline-none focus-visible:bg-light-gray
                      truncate
                    "
                  >
                    {sub.name}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <Link
          to={`/shop/${category.slug}`}
          onClick={onNavigate}
          className="
            block px-4 py-3 text-body-base font-semibold text-charcoal
            hover:text-royal-orange hover:bg-light-gray
            transition-colors duration-100
            focus-visible:outline-none focus-visible:bg-light-gray
          "
        >
          {category.name}
        </Link>
      )}
    </div>
  )
}

interface MobileNavLinkProps {
  to: string
  label: string
  onClick: () => void
}

const MobileNavLink: React.FC<MobileNavLinkProps> = ({ to, label, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className="
      block py-2.5 text-body-base text-charcoal
      hover:text-royal-orange transition-colors duration-100
      focus-visible:outline-none focus-visible:underline
    "
  >
    {label}
  </Link>
)